import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Clock, Users, CheckCircle, Star, Trophy, Sparkles, ChevronRight, Target, Zap } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { GeminiHeader } from '@/components/GeminiHeader';

interface Quest {
  id: string;
  worker_id: string;
  template_id: string | null;
  status: string;
  assigned_at: string;
  expires_at: string | null;
  xp_awarded: number;
  cluster_quest_id: string | null;
  response_data: Record<string, any> | null;
  quest_templates: {
    title: string;
    description: string;
    quest_type: string;
    xp_reward: number;
    content: Record<string, any>;
  } | null;
  cluster_quests: {
    title: string;
    target_value: number;
    current_value: number;
    status: string;
  } | null;
  intervention?: {
    id: string;
    title: string;
    message: string;
    category: string;
    xp_reward: number;
    content: Record<string, any>;
  } | null;
}

interface WorkerProfile {
  id: string;
  display_name: string;
  total_xp: number;
  consecutive_safe_days: number;
}

const QUEST_TYPE_ICONS: Record<string, string> = {
  survey: '📋',
  learning: '📚',
  commitment: '🎯',
  observation: '👀',
  challenge: '🏆',
};

export default function Quests() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<WorkerProfile | null>(null);
  const [quests, setQuests] = useState<Quest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/');
        return;
      }

      const { data: profileData } = await supabase
        .from('worker_profiles')
        .select('id, display_name, total_xp, consecutive_safe_days')
        .eq('user_id', session.user.id)
        .single();

      if (!profileData) {
        navigate('/');
        return;
      }

      setProfile(profileData);

      const { data: questsData, error } = await supabase
        .from('worker_quests')
        .select(`
          *,
          quest_templates (*),
          cluster_quests (*)
        `)
        .eq('worker_id', profileData.id)
        .order('assigned_at', { ascending: false });

      if (error) throw error;
      
      const questsWithInterventions = await Promise.all(
        (questsData || []).map(async (quest: any) => {
          if (!quest.quest_templates && quest.response_data?.intervention_id) {
            const { data: interventionData } = await supabase
              .from('interventions')
              .select('id, title, message, category, xp_reward, content')
              .eq('id', quest.response_data.intervention_id)
              .single();
            return { ...quest, intervention: interventionData };
          }
          return quest;
        })
      );
      
      setQuests(questsWithInterventions as Quest[]);
    } catch (error) {
      console.error('Error loading quests:', error);
      toast.error('Gagal memuat quest');
    } finally {
      setLoading(false);
    }
  };

  const pendingQuests = quests.filter(q => q.status === 'pending' || q.status === 'in_progress');
  const completedQuests = quests.filter(q => q.status === 'completed');

  const getTimeRemaining = (expiresAt: string | null) => {
    if (!expiresAt) return null;
    const diff = new Date(expiresAt).getTime() - Date.now();
    if (diff <= 0) return 'Expired';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 24) return `${hours} jam`;
    return `${Math.floor(hours / 24)} hari`;
  };

  const handleQuestClick = (quest: Quest) => {
    if (quest.status === 'expired') {
      toast.error('Quest ini sudah expired');
      return;
    }
    navigate(`/quests/${quest.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <GeminiHeader 
        title="Quest" 
        subtitle="Selesaikan quest untuk dapat XP"
      />

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 gap-3"
        >
          <div className="rounded-2xl bg-muted/50 dark:bg-muted/30 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                <Star className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{profile?.total_xp || 0}</p>
                <p className="text-xs text-muted-foreground">Total XP</p>
              </div>
            </div>
          </div>
          
          <div className="rounded-2xl bg-muted/50 dark:bg-muted/30 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                <Zap className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{profile?.consecutive_safe_days || 0}</p>
                <p className="text-xs text-muted-foreground">Hari Streak</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="w-full bg-muted/50 p-1 rounded-xl shadow-sm">
            <TabsTrigger value="pending" className="flex-1 rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-md">
              Tersedia ({pendingQuests.length})
            </TabsTrigger>
            <TabsTrigger value="completed" className="flex-1 rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-md">
              Selesai ({completedQuests.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-6 space-y-3 pb-24">
            <AnimatePresence>
              {pendingQuests.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-16"
                >
                  <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4 shadow-sm">
                    <Sparkles className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground font-medium">Belum ada quest tersedia</p>
                  <p className="text-sm text-muted-foreground mt-1">Cek lagi nanti!</p>
                </motion.div>
              ) : (
                pendingQuests.map((quest, index) => {
                  const questTitle = quest.quest_templates?.title || quest.intervention?.title || 'Quest';
                  const questDescription = quest.quest_templates?.description || quest.intervention?.message || '';
                  const questType = quest.quest_templates?.quest_type || quest.intervention?.category || 'learning';
                  const questXp = quest.quest_templates?.xp_reward || quest.intervention?.xp_reward || 0;
                  
                  return (
                    <motion.div
                      key={quest.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => handleQuestClick(quest)}
                      className="group cursor-pointer"
                    >
                      <div className="flex gap-4 p-4 rounded-2xl bg-card/80 hover:bg-card shadow-md shadow-black/5 dark:shadow-black/20 border border-border/50 hover:border-border hover:shadow-lg transition-all duration-200">
                                      <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center text-2xl shrink-0">
                                          {QUEST_TYPE_ICONS[questType] || QUEST_TYPE_ICONS['learning']}
                                        </div>

                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-start justify-between gap-2">
                                            <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-1">
                                              {questTitle}
                                            </h3>
                                            <Badge variant="secondary" className="shrink-0 font-medium">
                                              +{questXp} XP
                                            </Badge>
                                          </div>
                          
                          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                            {questDescription}
                          </p>

                          <div className="flex items-center gap-3 mt-3">
                            {quest.expires_at && (
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <Clock className="h-3.5 w-3.5" />
                                <span>{getTimeRemaining(quest.expires_at)}</span>
                              </div>
                            )}
                            {quest.cluster_quest_id && (
                              <div className="flex items-center gap-1.5 text-xs text-violet-600 dark:text-violet-400">
                                <Users className="h-3.5 w-3.5" />
                                <span>Team Challenge</span>
                              </div>
                            )}
                            <ChevronRight className="h-4 w-4 text-muted-foreground/50 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>

                          {quest.cluster_quests && (
                            <div className="mt-3 pt-3 border-t border-border/50">
                              <div className="flex items-center justify-between text-xs mb-1.5">
                                <span className="text-muted-foreground">Progress Tim</span>
                                <span className="font-medium">
                                  {Math.round(quest.cluster_quests.current_value)}%
                                </span>
                              </div>
                              <Progress 
                                value={quest.cluster_quests.current_value} 
                                className="h-1.5"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </AnimatePresence>
          </TabsContent>

          <TabsContent value="completed" className="mt-6 space-y-3 pb-24">
            {completedQuests.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4 shadow-sm">
                  <CheckCircle className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground font-medium">Belum ada quest selesai</p>
              </div>
            ) : (
              completedQuests.map((quest, index) => {
                const questTitle = quest.quest_templates?.title || quest.intervention?.title || 'Quest';
                return (
                  <motion.div 
                    key={quest.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.03 }}
                    className="flex gap-4 p-4 rounded-2xl bg-card/50 shadow-sm border border-border/30"
                  >
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center shrink-0 shadow-sm">
                      <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-medium text-foreground/70 line-clamp-1">
                          {questTitle}
                        </h3>
                        <span className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold shrink-0">
                          +{quest.xp_awarded} XP
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Selesai
                      </p>
                    </div>
                  </motion.div>
                );
              })
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}